import { OrganizationInfoCard } from '@/components/data-display/organization/organization-info-card';
import { FormErrorSummary } from '@/components/forms/form-error-summary';
import FormProvider from '@/components/forms/hook-form';
import { Industry, IOrderItem as IOrder, OrganizationCountry, OrganizationOwner } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Button, CardHeader, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MilestoneSelectorSection } from '../../sections/milestone-selector';
import { useBillingStore } from '../../store/billing.store';
import { OrganizationSelectOption, OrganizationsSelect } from '../input/organizations-select';
import { createBillingOrderFirstStepSchema } from './schema/createBillingOrderFirstStepSchema';
import { useOrganization, useOrganizations } from '../../../organizations/hooks';

type BillingOrderNewEditFirstStepFormProps = {
  currentOrder?: IOrder;
  currentOrganization?: { _id: string; name?: string };
  onNext: () => void;
  onCancel: () => void;
};

export const BillingOrderNewEditFirstStepForm = ({
  onNext,
  onCancel,
  currentOrganization,
}: BillingOrderNewEditFirstStepFormProps) => {
  const { t } = useTranslation();
  const setOrganization = useBillingStore((state) => state.setOrganization);
  const clearStore = useBillingStore((state) => state.clearStore);
  const organization = useBillingStore((state) => state.organization);
  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);

  const { data: organizations, isLoading: organizationsIsLoading } = useOrganizations(
    currentOrganization ? false : true
  );

  const organizationId = useMemo(() => {
    if (currentOrganization) {
      return currentOrganization._id;
    }

    if (!organization) {
      return '';
    }

    return organization?._id;
  }, [currentOrganization, organization]);

  const { organizationData } = useOrganization(organizationId);

  const organizationSelectOptions: OrganizationSelectOption[] = useMemo(() => {
    if (!organizations) {
      return [];
    }
    return organizations.data.map((orgItem) => ({
      _id: orgItem._id,
      name: `${orgItem.name}`,
      label: `${orgItem.name}`,
    }));
  }, [organizations?.data]);

  const organizationSelectDefaultValue = useMemo(() => {
    if (!organization) {
      return null;
    }

    return {
      _id: organization._id,
      name: organization.name,
      country: organization.country,
      owner: organization.owner,
      segment: organization.segment,
    };
  }, [organization]);

  const firstStepSchema = useMemo(() => createBillingOrderFirstStepSchema(t), [t]);

  const methods = useForm({
    resolver: yupResolver(firstStepSchema),
    mode: 'onSubmit',
    defaultValues: {
      organization: organizationSelectDefaultValue ?? undefined,
      selectedMilestones: [],
      onlySave: false,
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
    reset,
  } = methods;

  const onSubmit = handleSubmit(() => {
    onNext();
  });

  const handleChangeOrganization = useCallback(
    (event: SyntheticEvent<Element, Event>, newValue: OrganizationSelectOption | null) => {
      clearStore();
      if (newValue && typeof newValue === 'object' && '_id' in newValue) {
        setOrganization({
          _id: newValue._id,
          name: newValue.name,
        });
        reset({
          selectedMilestones: [],
          onlySave: false,
        });
      }
    },
    [clearStore, reset, setOrganization]
  );

  useEffect(() => {
    if (organizationId !== organization?._id) {
      clearStore();
    }

    if (organizationData && organizationData.data) {
      const { _id, name, country, owner, segment } = organizationData.data;

      const orgValue = {
        _id,
        name,
        country: country as OrganizationCountry,
        owner: owner as OrganizationOwner,
        segment: segment as Industry,
      };

      setValue('organization', orgValue);

      setOrganization(orgValue);
    }
  }, [organizationData, setOrganization, setValue]);

  useEffect(() => {
    setValue('selectedMilestones', selectedMilestones);
  }, [selectedMilestones]);

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Card sx={{ px: 2, pb: 3, mb: 3 }}>
          <CardHeader sx={{ px: 0, pb: 3 }} title={t('Company Information')} />
          <Box
            rowGap={3}
            columnGap={1}
            display="grid"
            sx={{ mb: 2 }}
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(1, 1fr)',
            }}
          >
            <OrganizationsSelect
              disabled={currentOrganization ? true : false}
              isLoading={organizationsIsLoading}
              options={organizationSelectOptions}
              onChange={handleChangeOrganization}
            />
          </Box>
        </Card>

        {organization && (
          <>
            <OrganizationInfoCard organizationData={organization} contacts={[]} />
            <MilestoneSelectorSection organizationId={organization._id} />
          </>
        )}

        <FormErrorSummary errors={errors} />
        <Grid container sx={{ mt: '8px' }}>
          <Grid item xs={4} sx={{ textAlign: 'left' }}>
            <Button
              disabled={isSubmitting}
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => {
                onCancel();
              }}
            >
              {t('Cancel')}
            </Button>
          </Grid>
          <Grid item xs={8} sx={{ textAlign: 'right' }}>
            <LoadingButton
              color="primary"
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              {t('Next')}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};
